/**
 * Domain allowlist for URLs that arrive from settings.
 *
 * Settings-supplied URLs are API-relative paths by default. A tenant that needs a
 * genuine third party — Google Fonts, a chatbot vendor — lists those hostnames in
 * `app_external_domain_allowlist` and only then may the matching URL settings carry
 * an absolute https URL.
 *
 * Built-in defaults (`DEFAULT_EXTERNAL_DOMAINS`) are always in the effective list so
 * Google Fonts works without configuration. The API setting widens that list.
 *
 * Effective allowlist = DEFAULT_EXTERNAL_DOMAINS ∪ parse(app_external_domain_allowlist).
 *
 * Trust note: the allowlist is itself a setting, so it does not defend against an API
 * admin who can edit settings — that actor can widen it. It defends against typos,
 * copy-pasted vendor URLs, and any path that sets one setting without setting this
 * one. This app image does not emit a Content-Security-Policy; nginx still sends
 * other static security headers (nosniff, Referrer-Policy, X-Frame-Options,
 * Permissions-Policy).
 */

/** Setting that carries the comma-separated hostname allowlist. */
export const EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY = 'app_external_domain_allowlist';

/**
 * Hosts every deployment may reach without naming them in the API setting.
 * Keep short — only hosts we would grant every tenant unprompted.
 */
export const DEFAULT_EXTERNAL_DOMAINS = ['fonts.googleapis.com', 'fonts.gstatic.com'] as const;

/** Labelled hostname, 1-253 chars, alphabetic TLD. Punycode (`xn--`) passes as normal labels. */
function isAlphaNum(code: number): boolean {
  return (code >= 48 && code <= 57) || (code >= 97 && code <= 122);
}

function isAlpha(code: number): boolean {
  return code >= 97 && code <= 122;
}

function isValidHostnameLabel(label: string, { tld }: { tld: boolean }): boolean {
  if (!label || label.length > 63) {
    return false;
  }

  for (let i = 0; i < label.length; i += 1) {
    const code = label.charCodeAt(i);

    if (tld) {
      if (!isAlpha(code)) {
        return false;
      }
    } else if (i === 0 || i === label.length - 1) {
      if (!isAlphaNum(code)) {
        return false;
      }
    } else if (!isAlphaNum(code) && code !== 45) {
      // hyphen allowed only in the middle of a non-TLD label
      return false;
    }
  }

  return tld ? label.length >= 2 : true;
}

function isValidAllowlistHostname(hostname: string): boolean {
  if (hostname.length < 1 || hostname.length > 253) {
    return false;
  }

  const labels = hostname.split('.');

  if (labels.length < 2) {
    return false;
  }

  for (let i = 0; i < labels.length; i += 1) {
    if (!isValidHostnameLabel(labels[i], { tld: i === labels.length - 1 })) {
      return false;
    }
  }

  return true;
}

const SCHEME_PREFIX_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//;

/** Domains from the last `configureExternalDomainAllowlist` call (API setting). */
let configuredFromSettings: readonly string[] = [];

/** Effective allowlist used by URL checks: defaults ∪ settings. */
let effectiveDomains: readonly string[] = [];

/**
 * Normalizes one allowlist entry to a bare hostname, or null when it is not one.
 *
 * Tolerates `https://host` and `host/path` because admins paste those, but only ever
 * keeps the hostname. Rejects wildcards: exact matching is the whole point, and
 * suffix matching is where allowlist bugs live (`evilfonts.googleapis.com` passing an
 * `endsWith` check). List every subdomain instead.
 */
function normalizeAllowlistEntry(entry: unknown): string | null {
  if (typeof entry !== 'string') {
    return null;
  }

  const candidate = entry.trim().toLowerCase().replace(SCHEME_PREFIX_PATTERN, '').replace(/\/.*$/, '');

  if (!candidate || candidate.includes('*') || candidate.includes('@')) {
    return null;
  }

  try {
    const { hostname, port } = new URL(`https://${candidate}`);

    // A port in the allowlist would imply port-scoped matching, which isValidExternalUrl
    // does not implement. Reject rather than silently ignore it.
    if (port) {
      return null;
    }

    // URL() lowercases and punycodes, so unicode and ASCII spellings converge here.
    return isValidAllowlistHostname(hostname) ? hostname : null;
  } catch {
    return null;
  }
}

/** Parses a comma/whitespace-separated allowlist value into deduplicated hostnames. */
export function parseExternalDomainAllowlist(value: unknown): string[] {
  const entries = Array.isArray(value) ? value : String(value ?? '').split(/[,\s;]+/);
  const hostnames = new Set<string>();

  for (const entry of entries) {
    const hostname = normalizeAllowlistEntry(entry);

    if (hostname) {
      hostnames.add(hostname);
    } else if (typeof entry === 'string' && entry.trim()) {
      console.warn(`[settings] Ignoring invalid "${EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY}" entry (expected a bare hostname):`, entry);
    }
  }

  return [...hostnames];
}

function recomputeEffectiveAllowlist(): readonly string[] {
  effectiveDomains = [...new Set([...DEFAULT_EXTERNAL_DOMAINS, ...configuredFromSettings])];
  return effectiveDomains;
}

/**
 * Sets the active allowlist from a settings value. Call before validating any URL
 * setting from the same payload — `normalizeAllApiSettings` and public bootstrap do this first.
 *
 * Returns the parsed **API** domains (not the defaults) for storage on the settings object.
 * URL checks use `getExternalDomainAllowlist()`, which is defaults ∪ this result.
 */
export function configureExternalDomainAllowlist(value: unknown): readonly string[] {
  configuredFromSettings = parseExternalDomainAllowlist(value);
  recomputeEffectiveAllowlist();
  return configuredFromSettings;
}

/** Effective hostnames for URL validation: DEFAULT_EXTERNAL_DOMAINS ∪ API setting. */
export function getExternalDomainAllowlist(): readonly string[] {
  return effectiveDomains;
}

/** Test helper — clears API-configured domains; defaults still apply. */
export function resetExternalDomainAllowlist(): void {
  configuredFromSettings = [];
  recomputeEffectiveAllowlist();
}

/**
 * Returns true when `value` is an absolute https URL on an allowlisted hostname.
 *
 * Deliberately strict beyond the hostname check: plaintext http, embedded
 * credentials, and non-443 ports are all rejected on an otherwise allowlisted host.
 */
export function isAllowlistedExternalUrl(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim() || !effectiveDomains.length) {
    return false;
  }

  let url: URL;

  try {
    url = new URL(value.trim());
  } catch {
    return false;
  }

  if (url.protocol !== 'https:' || url.username || url.password) {
    return false;
  }

  if (url.port && url.port !== '443') {
    return false;
  }

  return effectiveDomains.includes(url.hostname.toLowerCase());
}

// Apply defaults as soon as the module loads.
recomputeEffectiveAllowlist();
